import { Box, Portal } from '@chakra-ui/react';
import { useFocus } from 'modules/Editor/components/Focus';
import { useAppSelector } from 'modules/Editor/store';
import { ActiveWidgets } from 'modules/Editor/store/widgetControlSlice';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { memo } from 'react';
import { WidgetId } from 'types/idTypes';
import { MULTI_WIDGET_TOOLBAR_CONFIG } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { MultiEditWidgetTypes } from 'widgets/Widget.types';
import { MultiWidgetToolbar } from 'widgets/sdk/Toolbar/MultiWidgetToolbar';
import { useMultiEditConfig } from 'widgets/store/MultiEditConfigProvider';

/**
 * Check if any of the active widgets are of a widget type that supports multiple edit
 * @param activeWidgets
 * @returns {boolean}
 */
const hasMultiSelectWidgetType = (activeWidgets: ActiveWidgets) =>
  activeWidgets.some(
    (activeWidget) => !!MULTI_WIDGET_TOOLBAR_CONFIG[getWidgetTypeFromId(activeWidget.id) as MultiEditWidgetTypes],
  );

/**
 * Check if the multi widget toolbar should be displayed for the active widgets,
 * If every active widget has a custom display function and it returns false, the toolbar should not be displayed
 * E.g. multiple color icons are selected
 *
 * @param activeWidgets
 * @param syncRef
 * @returns {boolean}
 */
const shouldHideMultiWidgetToolbar = (
  activeWidgets: ActiveWidgets,
  getCustomDisplayFunction: (widgetId: WidgetId) => (() => boolean) | undefined,
) => {
  const shouldHideMultiWidgetToolbar = activeWidgets.every((activeWidget) => {
    const widgetId = activeWidget.id;

    const customWidgetDisplayMultiSelect = getCustomDisplayFunction(widgetId);
    if (typeof customWidgetDisplayMultiSelect === 'function' && !customWidgetDisplayMultiSelect()) return true;

    return false;
  });

  return shouldHideMultiWidgetToolbar;
};

export const MultiWidgetToolbarContainer = memo(() => {
  const { widgetToolbarPortalRef } = useFocus();
  const activeWidgets = useAppSelector(selectActiveWidgets);
  const { getCustomDisplayFunction } = useMultiEditConfig();

  if (!activeWidgets.length || activeWidgets.length === 1) return null;
  if (!hasMultiSelectWidgetType(activeWidgets)) return null;

  // dynamically check if the multiple widget toolbar should be displayed
  // Can be dynamic for certain widgets
  // (e.g. icon -> only mono color has the multiple widget toolbar - won't know this until icon data has loaded)
  if (shouldHideMultiWidgetToolbar(activeWidgets, getCustomDisplayFunction)) return null;

  return (
    <Portal containerRef={widgetToolbarPortalRef as React.RefObject<HTMLElement | null> | undefined}>
      <Box zIndex='toolbar'>
        <MultiWidgetToolbar />
      </Box>
    </Portal>
  );
});
MultiWidgetToolbarContainer.displayName = 'MultiWidgetToolbarContainer';
